import axios from 'axios'
import store from '@/store'
import Vue from 'vue'
import router from '@/router'
import { getBrowserFingerprint } from '@/utils/util'
import { Message } from 'element-ui'

const instance = axios.create({
  // 域名
  baseURL: process.env.VUE_APP_SERVER_URL,
  headers: {
    'Content-Type': 'application/json; charset=UTF-8'
  },
  // 5 分钟超时
  timeout: 300000,
  // 允许携带 cookie（确保 HttpOnly 的 refresh_token Cookie 能自动带给后端）
  withCredentials: true
})

// 并发请求挂载
Vue.prototype.$http = axios
const loading = null

// 🌟 无感刷新状态控制变量
let isRefreshing = false
let failedQueue = []

// 批量处理挂起队列
const processQueue = (error, token = null) => {
  failedQueue.forEach(promise => {
    if (error) {
      promise.reject(error)
    } else {
      promise.resolve(token)
    }
  })
  failedQueue = []
}

// ----------------------------------------------------
// 请求拦截器：注入 Access Token
// ----------------------------------------------------
instance.interceptors.request.use(
  config => {
    if (Vue.$cookies.get('fp') === null) {
      getBrowserFingerprint()
    }

    // 🌟 核心：从 Vuex 获取 Access Token 挂载到请求头
    const token = store.getters.token
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  },
  error => Promise.reject(error)
)

// ----------------------------------------------------
// 响应拦截器：实现 401 无感续期
// ----------------------------------------------------
// ----------------------------------------------------
// 响应拦截器：结合业务状态码精准控制无感续期与登出
// ----------------------------------------------------
instance.interceptors.response.use(
  response => {
    if (loading) loading.close()

    const code = response.status
    if ((code >= 200 && code < 300) || code === 304) {
      return Promise.resolve(response.data)
    } else {
      return Promise.reject(response)
    }
  },
  async error => {
    if (loading) loading.close()

    const originalRequest = error.config
    const requestUrl = originalRequest ? originalRequest.url : ''
    if (error.response) {
      const { status, data } = error.response
      // 提取后端的业务状态码（假设返回格式为 { code: 40101, msg: "..." }）
      const bizCode = data ? data.code : null

      // 🌟 1. 处理 HTTP 401 场景
      if (status === 401) {
        // ---------------------------------------------------------
        // 场景 A：Access Token 过期（40101），尝试无感续期
        // ---------------------------------------------------------
        if (bizCode === 40101 && !originalRequest._retry && !requestUrl.includes('/api/auth/refresh_token')) {
          // 如果当前已经在刷新中，将后续并发请求放入队列挂起
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject })
            })
              .then(newToken => {
                originalRequest.headers.Authorization = `Bearer ${newToken}`
                return instance(originalRequest)
              })
              .catch(err => Promise.reject(err))
          }

          // 标记开始刷新
          originalRequest._retry = true
          isRefreshing = true

          try {
            // 发起 refresh_token 请求（HttpOnly Cookie 自动携带）
            const res = await instance.post('/api/auth/refresh_token')

            // 根据后端登录/刷新返回的结构获取新的 accessToken
            const newAccessToken = res.data.accessToken

            // 更新 Vuex 内存中的 Access Token
            store.commit('SET_TOKEN', newAccessToken)

            // 释放挂起队列
            processQueue(null, newAccessToken)

            // 重发原始请求
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
            return instance(originalRequest)
          } catch (refreshErr) {
            // 🌟 Refresh Token 也失效（如过期/被踢），清空状态并强制登录
            processQueue(refreshErr, null)
            Vue.$cookies.remove('refresh_token')
            store.commit('USER_LOGOUT')

            router.replace({
              path: '/login',
              query: { redirect: router.currentRoute.fullPath }
            })
            return Promise.reject(refreshErr)
          } finally {
            isRefreshing = false
          }
        }

        // ---------------------------------------------------------
        // 场景 B：未认证/无 Token 访问受限接口（40102），直接踢回登录页
        // ---------------------------------------------------------
        if (bizCode === 40102 || requestUrl.includes('/api/auth/refresh_token')) {
          Vue.$cookies.remove('refresh_token')
          store.commit('USER_LOGOUT')
          router.replace({
            path: '/login',
            query: { redirect: router.currentRoute.fullPath }
          })
          return Promise.reject(error)
        }
      }

      // 2. 非 401 错误或未匹配的业务错误，展示提示 Message
      Message.error(data?.msg || `${requestUrl} ${error.message}`)
    } else {
      // 网络错误或请求超时
      Message.error(requestUrl + ' ' + error.message)
    }

    return Promise.reject(error)
  }
)

// HTTP 请求
export function post(url, data) {
  return instance({
    method: 'post',
    url,
    data: JSON.stringify(data)
  })
}

export function postForm(url, formData) {
  return instance({
    method: 'post',
    url,
    data: formData
  })
}

export function put(url, data) {
  return instance({
    method: 'put',
    url,
    data: JSON.stringify(data)
  })
}

export function delete0(url, data) {
  return instance({
    method: 'delete',
    url,
    data: JSON.stringify(data)
  })
}

export function get(url, params) {
  return instance({
    method: 'get',
    url,
    params
  })
}
